import {
  useRouteLoaderData,
  type ClientLoaderFunctionArgs,
} from 'react-router';
import { gql } from '@/__generated__';
import { preloadQuery } from '@/apollo/apollo-client';
import { FlagsQuery } from '@/feature-flags/flag-query';
import { authInfoFromSession, sessionContext, type AuthInfo } from '@/session';

export const UnreadNotificationsQuery = gql(`
  query UnreadNotificationsQuery($limit: Int!, $read: Boolean!) {
    notifications(input: {read: $read, limit: $limit, nextToken: null}) {
      items {
        id
      }
    }
  }
`);

export const meQuery = gql(`
  query meQuery {
    me {
      id
      username
      fullName
      createdAt
      bio
      acceptedTermsOfService
      acceptedPrivacyPolicy
      terminalAccessRequested
      protectedFields {
        email
        possibleUsernames
        intercomHmac
        stripeExpressSellerAccount {
          ...StripeSellerProfileFragment
        }
        coupons {
          couponId
          streamId
          discountAmountCents
          usedAt
        }
      }
      avatar {
        key
      }
      __typename
    }
  }
`);

const streamDecksQuery = gql(`
  query streamDecksQuery {
    streamDecks {
      id
      name
      collections {
        ...CustomCollectionFragment
      }
    }
  }
`);

const terminalTabsQuery = gql(`
  query terminalTabsQuery {
    terminalTabs {
      id
      name
      configuration
    }
  }
`);

export const appLoader = async ({ context }: ClientLoaderFunctionArgs) => {
  const session = context.get(sessionContext);

  switch (session.kind) {
    case 'guest':
      return unauthenticatedRootLoader();
    case 'incomplete':
    case 'user':
      return authenticatedRootLoader(authInfoFromSession(session));
    default: {
      const _exhaustive: never = session;
      return _exhaustive;
    }
  }
};

const unauthenticatedRootLoader = () => ({
  authenticated: false as const,
});

const authenticatedRootLoader = (authInfo: AuthInfo) => {
  preloadQuery(FlagsQuery);

  return {
    authenticated: true as const,
    unreadNotificationsQueryRef: preloadQuery(UnreadNotificationsQuery, {
      variables: { limit: 10, read: false },
    }),
    meQueryRef: preloadQuery(meQuery),
    streamDecksQueryRef: preloadQuery(streamDecksQuery),
    terminalTabsQueryRef: preloadQuery(terminalTabsQuery),
    authInfo,
  };
};

type LoaderData = Awaited<ReturnType<typeof appLoader>>;

export const useAppLoader = () =>
  useRouteLoaderData('routes/_app/route') as LoaderData;

export const useAuthenticatedAppLoader = () => {
  const data = useAppLoader();

  if (!data.authenticated) {
    throw new Error('Authenticated app loader data is not available');
  }

  return data;
};
